import { HttpClient } from '@angular/common/http';
import { Component, Injector, Input, OnChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'app/shared/auth.service';
import { BarcodeService } from 'app/shared/barcode.service';
import { EmailService } from 'app/shared/email.service';
import { LabelFormat, PrintFns, SelectedPrinters } from 'app/shared/print-fns';
import { ScaleService } from 'app/shared/scale.service';
import { ShippingService } from 'app/shipping/shipping.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'environments/environment';

//TODO move link for print utility

@Component({
  selector: 'printer-setup',
  templateUrl: './printer-setup.component.html',
  standalone: false,
})
export class PrinterSetupComponent implements OnChanges {
  @Input() isOpen!: boolean;

  barcodePrinter!: string;
  invoicePrinter!: string;
  labelPrinter!: string;
  labelFormat!: LabelFormat;
  labelFormats: LabelFormat[] = ['pdf', 'zpl'];
  clientPrinters!: any[];
  errorMessage: string = 'Connecting to QZ printers...';
  qbStatus: string = '';
  showLink = false;

  scaleResult: string = '';
  scaleSupported: boolean;

  constructor(
    private shippingService: ShippingService,
    private barcodeService: BarcodeService,
    private authService: AuthService,
    private scaleService: ScaleService,
    private injector: Injector,
  ) {
    this.scaleSupported = this.scaleService.isSupported;
  }

  ngOnChanges() {
    // get the list of printers after the panel is opened the first time
    if (this.isOpen) {
      this.getPrinterNames();

      // get previously chosen printers
      const sp = PrintFns.getSelectedPrinters() || ({} as SelectedPrinters);
      this.barcodePrinter = sp.barcodePrinter;
      this.invoicePrinter = sp.invoicePrinter;
      this.labelPrinter = sp.labelPrinter;
      this.labelFormat = sp.labelFormat;
    }
  }

  async getPrinterNames() {
    const started = await PrintFns.startQZ();
    if (started) {
      const installed = await PrintFns.getInstalledPrinters();
      if (!installed || !installed.length) {
        this.errorMessage = 'No printers found!';
      } else {
        this.clientPrinters = installed.map((x) => (x.name ? x.name : x));
        this.errorMessage = '';
      }
    } else {
      this.errorMessage = 'QZ Tray (print manager) could not start!';
      this.showLink = true;
    }
  }

  save() {
    // save selected printer names in local storage
    const sp: SelectedPrinters = {
      barcodePrinter: this.barcodePrinter,
      invoicePrinter: this.invoicePrinter,
      labelPrinter: this.labelPrinter,
      labelFormat: this.labelFormat,
    };
    PrintFns.saveSelectedPrinters(sp);
  }

  testBarcode() {
    this.barcodeService.printTestBarcode();
  }

  async testShippingLabel() {
    this.shippingService.printTestLabel();
  }

  async testEmail() {
    const emailAddress = this.authService.getUser()?.email;
    const emailService = this.injector.get(EmailService);
    const toastr = this.injector.get(ToastrService);
    toastr.info('Sending to ' + emailAddress, 'Email Status');
    const body = '<h1>Testing</h1><p>This is a test</p><hr><p><i>Did it work?</i></p>';
    const status = await emailService.sendBasicEmail(emailAddress, 'Test Message', body);

    if (status === 'accepted' || status === 'stored' || status === 'delivered') {
      toastr.info(status, 'Email Status');
    } else {
      toastr.error(status, 'Email Status');
    }
  }

  async testScale() {
    const toastr = this.injector.get(ToastrService);
    this.scaleResult = '';

    try {
      const connected = await this.scaleService.connect();
      if (!connected) {
        this.scaleResult = 'No DYMO scale detected. Connect a scale via USB and try again.';
        return;
      }

      toastr.info('Reading scale...', 'Scale');
      const reading = await this.scaleService.readWeight();

      const stabilityNote = reading.stable ? 'stable' : 'unstable';
      const ozValue = reading.unit === 'oz' ? reading.weight : ScaleService.convertWeight(reading.weight, reading.unit, 'oz');
      const lbValue = ScaleService.convertWeight(reading.weight, reading.unit, 'lb');
      const grValue = reading.unit === 'gr' ? reading.weight : ScaleService.convertWeight(reading.weight, reading.unit, 'gr');

      this.scaleResult = `${this.scaleService.deviceName}: ${ozValue} oz / ${lbValue} lb / ${grValue} gr (${stabilityNote})`;
      toastr.success(this.scaleResult, 'Scale');
    } catch (err: any) {
      this.scaleResult = err.message || 'Scale read failed.';
      toastr.error(this.scaleResult, 'Scale');
    }
  }

  async testSms() {
    const cellNumber = this.authService.getUser()?.cellNumber;
    const emailService = this.injector.get(EmailService);
    const toastr = this.injector.get(ToastrService);

    if (!cellNumber) {
      toastr.error('No cell number configured for your account', 'SMS Status');
      return;
    }

    toastr.info('Sending to ' + cellNumber, 'SMS Status');
    const result = await emailService.sendTestSms(cellNumber);

    if (result.status === 'success') {
      toastr.success(result.message, 'SMS Status');
    } else {
      toastr.error(result.message, 'SMS Status');
    }
  }

  async testSharePoint() {
    const http = this.injector.get(HttpClient);
    const toastr = this.injector.get(ToastrService);

    toastr.info('Testing SharePoint connection...', 'SharePoint');

    try {
      const url = environment.apiRoot + 'api/pdf/TestSharePoint';
      const result = await firstValueFrom(http.get<{ status: string; message: string; webUrl?: string }>(url));

      if (result.status === 'success') {
        toastr.success(
          `Connected successfully.<br>Test file: <a href="${result.webUrl}" target="_blank" class="bluelink">${result.webUrl}</a>`,
          'SharePoint',
          { enableHtml: true, timeOut: 10000 }
        );
      } else {
        toastr.error(result.message, 'SharePoint');
      }
    } catch (err: any) {
      const message = err.error?.message || err.message || 'Unknown error';
      toastr.error(message, 'SharePoint');
    }
  }
}
